import { gql, useApolloClient } from "@apollo/client";
import { CAlert, CSpinner } from "@coreui/react-pro";
import { useHandleSignInCallback, useLogto } from "@logto/react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setAuthToken } from "../../auth";
import { logtoApiResource } from "../../config/logto";
import {
  AUTH_STATE,
  authError,
  authState,
  clearReturnTo,
  readReturnTo,
} from "../../graphql/policies/auth";

/**
 * Logto sign-in callback handler.
 *
 * Mounted at `/callback`. The SDK consumes the authorisation code,
 * exchanges it for tokens, and persists them. We then:
 *
 *   1. Pull the Logto access token out of the SDK.
 *   2. POST `ExchangeLogtoToken(logtoToken)` to monitor-api. The server
 *      verifies the Logto JWT, resolves / creates the local myaccount
 *      row, mints a monitor JWT, and sets an httpOnly refresh-token
 *      cookie on the response.
 *   3. Store the returned monitor JWT in sessionStorage and forget the
 *      Logto access token. From here on, every GraphQL call carries the
 *      monitor JWT — the Logto access token is never sent to monitor-api.
 *   4. Redirect to the originally-requested URL (held in sessionStorage
 *      by Login.js) or to the dashboard root.
 */

const EXCHANGE_MUTATION = gql`
  mutation ExchangeLogtoToken($logtoToken: String!) {
    ExchangeLogtoToken(logtoToken: $logtoToken) {
      jwt
      accessTtl
    }
  }
`;

const Callback = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useLogto();
  const apollo = useApolloClient();
  const [exchangeError, setExchangeError] = useState(null);
  // Tracks whether `completeSignIn` actually reached the navigate
  // step. Used to suppress the error UI in the StrictMode dev case
  // where `useHandleSignInCallback` fires twice — the first
  // invocation consumes the auth code and succeeds, the second
  // tries the now-consumed code and sets `error`. Without this
  // guard, the CAlert flashes briefly before the SPA navigation
  // tears the page down.
  const [exchangeSucceeded, setExchangeSucceeded] = useState(false);
  const exchangedRef = useRef(false);

  const completeSignIn = async () => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    try {
      const logtoToken = await getAccessToken(logtoApiResource());
      if (!logtoToken) {
        throw new Error("Logto sign-in did not yield an access token");
      }

      const { data, errors } = await apollo.mutate({
        mutation: EXCHANGE_MUTATION,
        variables: { logtoToken },
        // The authLink would normally attach whatever access token we
        // currently hold; for this one call we explicitly do NOT want
        // to send the monitor JWT (we don't have one yet), so the
        // empty header is exactly what we want.
        context: { headers: { authorization: "" } },
        errorPolicy: "all",
      });

      if (errors && errors.length > 0) {
        throw new Error(errors[0]?.message ?? "Exchange failed");
      }

      const jwt = data?.ExchangeLogtoToken?.jwt;
      if (!jwt) {
        throw new Error("Exchange response carried no monitor JWT");
      }

      setAuthToken(jwt);
      // Clear any "you were signed out" message from the session that
      // sent them here; it is stale the moment they are back in.
      authError("");
      authState(AUTH_STATE.LOGGED_IN);
      setExchangeSucceeded(true);

      // The in-memory var did not survive the OIDC redirect; the
      // sessionStorage mirror `captureReturnTo` wrote is the copy that
      // did. `readReturnTo` re-validates it before we navigate.
      const target = readReturnTo() || "/";
      clearReturnTo();
      navigate(target, { replace: true });
    } catch (e) {
      // biome-ignore lint/suspicious/noConsole: deliberate diagnostic for auth callback failures
      console.error("Monitor JWT exchange failed:", e);
      setExchangeError(e?.message ?? "Exchange failed");
    }
  };

  const { isLoading, error } = useHandleSignInCallback(() => {
    // Don't await here: useHandleSignInCallback expects a sync-or-void
    // return, and we want React to render the spinner while the
    // exchange runs in the background.
    completeSignIn();
  });

  useEffect(() => {
    if (error) {
      // biome-ignore lint/suspicious/noConsole: deliberate diagnostic for auth callback failures
      console.error("Logto sign-in callback error:", error);
    }
  }, [error]);

  if ((error || exchangeError) && !exchangeSucceeded) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CAlert color="danger">
          Authentication failed. Please <a href="/login">try again</a>.
        </CAlert>
      </div>
    );
  }

  return (
    <div
      style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
    >
      <CSpinner color="primary" /> Completing sign-in…
    </div>
  );
};

export default Callback;
